/**
 * Personal Mode Landing Page
 *
 * A user-centric landing page that communicates:
 * "I can own my work history, carry my AI memory, and let an agent safely propose actions for me."
 *
 * Governance remains under the hood.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { 
  Brain, 
  History, 
  ShieldCheck, 
  Github,
  ArrowRight,
  Sparkles,
  FolderOpen,
  Download,
  Lock
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface PersonalLandingPageProps {
  onSignIn: () => void;
  isDemoMode?: boolean;
}

export const PersonalLandingPage: React.FC<PersonalLandingPageProps> = ({ onSignIn }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#16213e] text-white">
      {/* Top Nav */}
      <nav className="border-b border-gray-800 bg-[#0a0a0f]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Sparkles className="w-6 h-6 text-purple-400" />
            <span className="text-xl font-bold">XableAI</span>
          </div>
          <div className="flex items-center space-x-6">
            <Link
              to="/"
              className="text-gray-300 hover:text-white transition-colors"
            >
              Home
            </Link>
            <Link
              to="/system"
              className="text-gray-300 hover:text-white transition-colors"
            >
              Decision Verification System
            </Link>
            <Link
              to="/verify"
              className="text-gray-300 hover:text-white transition-colors"
            >
              Verify a Package
            </Link>
            <button
              onClick={onSignIn}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all duration-200"
            >
              Sign In
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-6 py-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            Your AI Workspace.
            <br />
            Owned by You.
          </h1>
          <p className="text-xl text-white mb-4 max-w-2xl mx-auto font-medium">
            Turn your work into a portable, verifiable history—and let an AI safely propose changes for it.
          </p>
          <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
            Watch Xable propose, simulate, and review changes safely.
          </p>
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={onSignIn}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all duration-200 flex items-center space-x-2"
            >
              <span>Try a real progression demo</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="https://github.com/apps/xable/installations/new"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-gray-800 text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-700 transition-all duration-200"
            >
              <Github className="w-5 h-5" />
              <span>Connect GitHub</span>
            </a>
          </div>
          <p className="text-sm text-gray-500 mt-4">Takes 30 seconds. No setup required.</p>
        </motion.div>
      </section>

      {/* Proof Playground CTA */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-700 rounded-xl p-8"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-2xl font-semibold mb-2">Try a real scenario</h3>
              <p className="text-gray-400 mb-4">Review a change and see what happens before you apply it.</p>
              <div className="space-y-1 text-sm text-gray-300 mb-6">
                <div>Beginner: single bug changes</div>
                <div>Advanced: multi-system reasoning</div>
                <div>Korros: know when not to act</div>
                <div>Kairos: know when it's safe to proceed</div>
              </div>
            </div>
            <a
              href="https://github.com/korzent/xable-playground"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors whitespace-nowrap"
            >
              <span>Open playground</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </section>

      {/* Your Work */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-3xl font-bold mb-12 text-center">Your Workspace</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Brain,
                title: 'Your Agent',
                description: 'Let an AI agent propose actions—and show what\'s safe to review before you apply.',
                color: 'purple',
                badge: null
              },
              {
                icon: History,
                title: 'Your Work',
                description: 'Own your complete work history with verified contributions that never disappear.',
                color: 'blue',
                badge: 'Shared repos coming soon'
              },
              {
                icon: FolderOpen,
                title: 'Your Memory',
                description: 'Carry your AI memory anywhere. Portable artifacts that work across platforms.',
                color: 'green',
                badge: null
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className={`bg-gray-800/50 border border-gray-700 rounded-xl p-6 text-center hover:border-${item.color}-500 transition-all duration-200`}
              >
                <item.icon className={`w-12 h-12 text-${item.color}-400 mx-auto mb-4`} />
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
                {item.badge && (
                  <div className="mt-3 inline-block text-xs text-purple-400 bg-purple-900/30 px-3 py-1 rounded-full border border-purple-700">
                    {item.badge}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Safety & Ownership */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <h2 className="text-3xl font-bold mb-12 text-center">Built for Safety & Ownership</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: ShieldCheck,
                title: 'Safe Proposals',
                description: 'Every proposal is simulated before you review and approve—so you see what will happen first.'
              },
              {
                icon: Lock,
                title: 'Your Data',
                description: 'Your work and AI memory belong to you. Export anytime, use anywhere.'
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: index === 0 ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="bg-gray-800/50 border border-gray-700 rounded-xl p-8"
              >
                <item.icon className="w-8 h-8 text-purple-400 mb-4" />
                <h3 className="text-2xl font-semibold mb-4">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How It Works */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <h2 className="text-3xl font-bold mb-12 text-center">How It Works</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '1', title: 'Connect GitHub', description: 'Import your work history' },
              { step: '2', title: 'Build Memory', description: 'AI builds a memory from your work' },
              { step: '3', title: 'Propose Actions', description: 'Agent suggests next steps' },
              { step: '4', title: 'Review Safely', description: 'Simulated outcomes, your approval' }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Export CTA */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-700 rounded-xl p-8 text-center"
        >
          <Download className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">Take Your Data With You</h2>
          <p className="text-gray-300 mb-6">
            Export your AI memory anytime.<br />Portable, verifiable, and yours forever.
          </p>
          <button
            onClick={onSignIn}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
          >
            Start Building Your Memory
          </button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800 mt-16 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center text-gray-500 text-sm">
          <p>XableAI — Your AI Workspace. Owned by You.</p>
        </div>
      </footer>
    </div>
  );
};
